import React, { useEffect, useState } from 'react';
import CommonModalLayout from '../Layouts/CommonModalLayout';
import { buttonStyles, inputStyles, channelOptionsMenuItemButton } from '../../styles/commonStyles';
import { LinkIcon } from '@heroicons/react/24/solid';
import Separator from '../Separator';
import { TrashIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { updateCollection, deleteCollection, getCollectionById } from '../../api/collections';
import { useDispatch } from 'react-redux';
import { setChannelUpdated, setChannelDeleted } from '../../redux/actions';
import { useQueryClient, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import DeleteChannelModal from '../Modals/DeleteChannelModal';
import CircleAvatar from '../Channel/CircleAvatar';
import Cropper from 'react-easy-crop';
import RotateIcon from '../Icons/RotateIcon';

const EditChannelModal = ({ isOpen, onClose, channelId, channelData }) => {
    const { t } = useTranslation();
    const [channelName, setChannelName] = useState(channelData?.name || '');
    const [description, setDescription] = useState(channelData?.description || '');
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [avatarCropOpen, setAvatarCropOpen] = useState(false);
    const [avatarFile, setAvatarFile] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);

    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const { data: fetchedChannelData, isLoading, error } = useQuery(
        ['channel', channelId],
        () => getCollectionById(channelId),
        {
            enabled: !channelData,
            staleTime: 1000 * 60 * 5,
            cacheTime: 1000 * 60 * 10,
        }
    );

    useEffect(() => {
        if (fetchedChannelData) {
            setChannelName(fetchedChannelData.name);
            setDescription(fetchedChannelData.description || '');
        }
    }, [fetchedChannelData]);

    const handleSave = async () => {
        try {
            await updateCollection(channelId, { name: channelName, description });
            queryClient.setQueryData(['channel', channelId], { ...fetchedChannelData, name: channelName, description });
            queryClient.invalidateQueries('channels');
            dispatch(setChannelUpdated(true));
            onClose();
        } catch (error) {
            alert(`Error: ${error.message}`);
        }
    };

    const openDeleteModal = () => setDeleteModalOpen(true);
    const closeDeleteModal = () => setDeleteModalOpen(false);

    const handleDelete = async () => {
        try {
            await deleteCollection(channelId);
            dispatch(setChannelDeleted(true));
            queryClient.invalidateQueries('channels');
            navigate('/mychannels');
            onClose();
            setDeleteModalOpen(false);
        } catch (error) {
            alert(`Error: ${error.message}`);
        }
    };

    const handleAvatarClick = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = (e) => {
            const file = e.target.files[0];
            if (file) {
                setAvatarFile(URL.createObjectURL(file));
                setAvatarCropOpen(true);
            }
        };
        input.click();
    };

    const handleSaveAvatar = async () => {
        try {
            // Создаем canvas для обрезки изображения
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();
            
            img.onload = async () => {
                const size = 200; // Размер аватарки
                canvas.width = size;
                canvas.height = size;
                
                // Рисуем обрезанное изображение
                ctx.save();
                ctx.translate(size / 2, size / 2);
                ctx.rotate((rotation * Math.PI) / 180);
                ctx.scale(zoom, zoom);
                ctx.drawImage(img, -size / 2, -size / 2, size, size);
                ctx.restore();
                
                // Конвертируем в blob
                canvas.toBlob(async (blob) => {
                    if (blob) {
                        const formData = new FormData();
                        formData.append('file', blob, 'avatar.jpg');
                        
                        // Используем HttpClient для правильной авторизации
                        const { httpClient } = await import('../../api/httpClient');
                        const client = new httpClient();
                        
                        const response = await client.request('/uploads/upload', {
                            method: 'POST',
                            body: formData,
                        });
                        
                        if (response.ok) {
                            const data = await response.json();
                            // Обновляем аватарку канала
                            await updateCollection(channelId, { 
                                name: channelName, 
                                description,
                                avatar_url: data.cloud_url 
                            });
                            queryClient.invalidateQueries(['channel', channelId]);
                            dispatch(setChannelUpdated(true));
                            setAvatarCropOpen(false);
                            setAvatarFile(null);
                        } else {
                            alert('Ошибка загрузки аватарки');
                        }
                    }
                }, 'image/jpeg', 0.9);
            };
            
            img.src = avatarFile;
        } catch (error) {
            console.error('Error saving avatar:', error);
            alert('Ошибка сохранения аватарки');
        }
    };

    return (
        <>
            <CommonModalLayout isOpen={isOpen} onClose={onClose} title={t('channelModal.editChannelTitle')}>
                <div className="px-0 py-0">
                    <div className="flex flex-row items-center gap-6 mb-4">
                        <CircleAvatar name={channelName} size="extraLarge" edit={true} onClick={handleAvatarClick} />
                        <div className="flex-1">
                            <label htmlFor="channelName" className="mb-2 block text-sm font-medium">{t('channelModal.namePlaceholder')}</label>
                            <input
                                id="channelName"
                                type="text"
                                value={channelName}
                                onChange={(e) => setChannelName(e.target.value)}
                                className={inputStyles + ' w-full h-14 text-lg'}
                                placeholder={t('channelModal.namePlaceholder')}
                                style={{ verticalAlign: 'middle' }}
                            />
                        </div>
                    </div>
                    <div className="mb-0 mt-4">
                        <label htmlFor="channelDescription" className="mb-2 block text-sm font-medium">{t('channel.description') || 'Описание канала'}</label>
                        <textarea
                            id="channelDescription"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            className={inputStyles + ' min-h-[80px]'}
                            placeholder={t('channel.description_placeholder') || 'Описание, ссылки, контакты, QR...'}
                        />
                    </div>
                    <Separator />
                    <button className={`${channelOptionsMenuItemButton} py-[8px] flex items-center w-full`}>
                        <LinkIcon className="w-5 h-5 mr-3" />
                        {t('channelModal.inviteLinkButton')}
                    </button>
                    <Separator />
                    <button
                        onClick={openDeleteModal}
                        className={`${channelOptionsMenuItemButton} py-[8px] flex items-center w-full text-red-600`}
                    >
                        <TrashIcon className="w-5 h-5 mr-3" />
                        {t('channelModal.deleteChannelButton')}
                    </button>
                    <Separator />
                    <div className="flex justify-end space-x-2 pt-2">
                        <button onClick={onClose} className={buttonStyles}>{t('channelModal.cancelButton')}</button>
                        <button onClick={handleSave} className={buttonStyles}>{t('channelModal.saveButton')}</button>
                    </div>
                </div>
            </CommonModalLayout>

            <DeleteChannelModal
                isOpen={isDeleteModalOpen}
                onClose={closeDeleteModal}
                onDelete={handleDelete}
                channelTitle={channelName}
                channelDescription={t('channelModal.deleteConfirmationMessage')}
            />

            {avatarCropOpen && (
                <CommonModalLayout 
                    isOpen={avatarCropOpen} 
                    onClose={() => setAvatarCropOpen(false)} 
                    title={t('channelModal.avatarCropTitle') || 'Установить фотографию'}
                    customStyles="fixed left-1/2 top-[30px] transform -translate-x-1/2 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 w-[350px] md:w-[450px] min-h-[520px] z-[9999] border border-gray-300 dark:border-gray-700 transition-all [@media(max-width:400px)]:w-[calc(100vw-20px)] [@media(max-width:400px)]:max-w-[calc(100vw-20px)]"
                >
                    <div className="flex flex-col items-center">
                        <div className="relative w-full aspect-square bg-white dark:bg-gray-600/60">
                            {avatarFile && (
                                <Cropper
                                    image={avatarFile}
                                    crop={crop}
                                    zoom={zoom}
                                    aspect={1}
                                    rotation={rotation}
                                    onCropChange={setCrop}
                                    onZoomChange={setZoom}
                                    onRotationChange={setRotation}
                                    cropShape="round"
                                />
                            )}
                        </div>
                        <div className="flex gap-2 mt-4">
                            <button className={buttonStyles} onClick={() => setAvatarCropOpen(false)}>{t('cancel', 'Отмена')}</button>
                            <button className={buttonStyles} onClick={() => setRotation(r => r + 90)} title="Повернуть">
                                <RotateIcon className="w-6 h-6" />
                            </button>
                            <button className={buttonStyles} onClick={handleSaveAvatar}>{t('setPhoto', 'Установить фотографию')}</button>
                        </div>
                    </div>
                </CommonModalLayout>
            )}
        </>
    );
};

export default EditChannelModal;
